import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, Trash2, Mail, Phone, MessageCircle, Video, Plus, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useLead, useUpdateLead, useDeleteLead } from '../../api/leads'
import { useMeetings } from '../../api/meetings'
import LeadComments from './LeadComments'
import { SpecificationVersions } from './SpecificationVersions'
import ConvertWizardModal from './ConvertWizardModal'
import { useUsers } from '../../api/users'
import { LeadStatusBadge, LeadSourceBadge, Badge } from '../ui/Badge'
import { ConfirmModal } from '../ui/ConfirmModal'
import { LostReasonModal } from '../pipeline/LostReasonModal'
import { cn, formatDate } from '../../lib/utils'
import type { LeadStatus, LostReason } from '@1130-studio/shared'

interface LeadDetailModalProps {
  leadId: string
  isOpen: boolean
  onClose: () => void
}

const statusOptions: { value: LeadStatus; label: string }[] = [
  { value: 'new', label: 'Новая' },
  { value: 'bd_assigned', label: 'В работе BD' },
  { value: 'call_scheduled', label: 'Созвон назначен' },
  { value: 'call_done', label: 'Созвон проведён' },
  { value: 'qualified', label: 'Квалифицирована' },
  { value: 'estimation', label: 'Оценка' },
  { value: 'converted', label: 'Конвертирована' },
  { value: 'lost', label: 'Потеряна' },
]

const statusPillColors: Record<LeadStatus, { active: string; inactive: string }> = {
  new: {
    active: 'bg-accent-lime/20 text-accent-lime border-accent-lime/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  bd_assigned: {
    active: 'bg-accent-blue/20 text-accent-blue border-accent-blue/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  call_scheduled: {
    active: 'bg-accent-violet/20 text-accent-violet border-accent-violet/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  call_done: {
    active: 'bg-accent-violet/20 text-accent-violet border-accent-violet/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  qualified: {
    active: 'bg-accent-blue/20 text-accent-blue border-accent-blue/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  estimation: {
    active: 'bg-accent-orange/20 text-accent-orange border-accent-orange/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  converted: {
    active: 'bg-accent-green/20 text-accent-green border-accent-green/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
  lost: {
    active: 'bg-red-500/20 text-red-400 border-red-500/30',
    inactive: 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-text-secondary',
  },
}

export default function LeadDetailModal({ leadId, isOpen, onClose }: LeadDetailModalProps) {
  const navigate = useNavigate()
  const { data: lead, isLoading } = useLead(leadId)
  const updateLead = useUpdateLead()
  const deleteLead = useDeleteLead()
  const { data: usersList } = useUsers()
  const { data: meetingsData } = useMeetings({ leadId, pageSize: 5 })

  const [specification, setSpecification] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showLostReason, setShowLostReason] = useState(false)
  const [showConvertWizard, setShowConvertWizard] = useState(false)

  useEffect(() => {
    if (lead) {
      setSpecification(lead.specification || '')
    }
  }, [lead])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !showDeleteConfirm) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose, showDeleteConfirm])

  const handleStatusChange = (status: LeadStatus) => {
    if (lead && lead.status !== status) {
      if (status === 'lost') {
        setShowLostReason(true)
        return
      }
      updateLead.mutate({ id: leadId, data: { status } })
    }
  }

  const handleLostReasonConfirm = (reason: LostReason, comment?: string) => {
    updateLead.mutate(
      { id: leadId, data: { status: 'lost' as LeadStatus, lostReason: reason, lostComment: comment } },
      { onSuccess: () => setShowLostReason(false) }
    )
  }

  const handleAssign = (userId: string | null) => {
    updateLead.mutate({ id: leadId, data: { assignedTo: userId } })
  }

  const handleConvert = () => {
    setShowConvertWizard(true)
  }

  const handleSpecificationBlur = () => {
    if (lead && specification !== (lead.specification || '')) {
      updateLead.mutate({ id: leadId, data: { specification } })
    }
  }

  const handleDelete = () => {
    deleteLead.mutate(leadId, {
      onSuccess: () => {
        setShowDeleteConfirm(false)
        onClose()
      },
    })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative bg-bg-card border border-border-medium backdrop-blur-glass w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-accent-lime border-t-transparent rounded-full" />
              </div>
            ) : !lead ? (
              <div className="text-center py-20 text-text-tertiary">
                Заявка не найдена
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <div className="flex items-center gap-2 min-w-0 pr-4">
                    <h2 className="text-xl font-bold text-white truncate">{lead.name}</h2>
                    <span className="text-sm text-text-tertiary font-mono flex-shrink-0">
                      #{lead.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center text-text-secondary hover:text-red-400 transition-colors"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onClose}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Status & Source Badges */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <LeadStatusBadge status={lead.status} />
                    <LeadSourceBadge source={lead.source} />
                  </div>

                  {/* Status Change Buttons */}
                  <div>
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Изменить статус
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {statusOptions.map((option) => {
                        const isActive = lead.status === option.value
                        const colors = statusPillColors[option.value]
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleStatusChange(option.value)}
                            disabled={updateLead.isPending}
                            className={cn(
                              'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                              isActive ? colors.active : colors.inactive,
                              updateLead.isPending && 'opacity-50 cursor-not-allowed'
                            )}
                          >
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* BD Assignment */}
                  <div>
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Ответственный BD
                    </label>
                    <select
                      value={lead.assignedTo || ''}
                      onChange={(e) => handleAssign(e.target.value || null)}
                      className="w-full px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-lime/50"
                    >
                      <option value="">Не назначен</option>
                      {usersList?.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.username}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="h-px bg-white/10" />

                  {/* Preferred Contact */}
                  {lead.preferredContact && lead.preferredContact.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                        Предпочтительный способ связи
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {lead.preferredContact.map((method) => (
                          <Badge key={method} variant="default" className="text-[11px]">
                            {method === 'email' ? 'Email' : method === 'phone' ? 'Телефон' : method === 'telegram' ? 'Telegram' : method === 'whatsapp' ? 'WhatsApp' : method}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div>
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Контакты
                    </label>
                    <div className="space-y-2">
                      {lead.email && (
                        <div className="flex items-center gap-2 text-sm text-text-primary">
                          <Mail className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                          <a
                            href={`mailto:${lead.email}`}
                            className="hover:text-accent-lime transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {lead.email}
                          </a>
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center gap-2 text-sm text-text-primary">
                          <Phone className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                          <a
                            href={`tel:${lead.phone}`}
                            className="hover:text-accent-lime transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {lead.phone}
                          </a>
                        </div>
                      )}
                      {lead.telegram && (
                        <div className="flex items-center gap-2 text-sm text-text-primary">
                          <MessageCircle className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                          <a
                            href={`https://t.me/${lead.telegram.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-accent-lime transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            @{lead.telegram.replace('@', '')}
                          </a>
                        </div>
                      )}
                      {!lead.email && !lead.phone && !lead.telegram && (
                        <p className="text-sm text-text-tertiary">Контакты не указаны</p>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  {lead.message && (
                    <>
                      <div className="h-px bg-white/10" />
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                          Сообщение
                        </label>
                        <div className="text-sm text-text-primary bg-white/5 rounded-lg p-4 italic">
                          &ldquo;{lead.message}&rdquo;
                        </div>
                      </div>
                    </>
                  )}

                  <div className="h-px bg-white/10" />

                  {/* Comments */}
                  <LeadComments leadId={leadId} />

                  <div className="h-px bg-white/10" />

                  {/* Meetings */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        Встречи
                      </label>
                      <button
                        onClick={() => {
                          onClose()
                          navigate(`/meetings?leadId=${leadId}`)
                        }}
                        className="flex items-center gap-1 text-xs text-accent-lime hover:text-accent-lime/80 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Назначить
                      </button>
                    </div>
                    {meetingsData?.items && meetingsData.items.length > 0 ? (
                      <div className="space-y-2">
                        {meetingsData.items.map((meeting) => (
                          <button
                            key={meeting.id}
                            onClick={() => {
                              onClose()
                              navigate(`/meetings/${meeting.id}`)
                            }}
                            className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-left group"
                          >
                            <Video className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-text-primary truncate">{meeting.title}</div>
                              <div className="text-xs text-text-tertiary">
                                {new Date(meeting.scheduledAt).toLocaleDateString('ru-RU', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                                {' · '}
                                {meeting.durationMinutes} мин
                              </div>
                            </div>
                            <Badge
                              variant={
                                meeting.status === 'scheduled' ? 'info'
                                  : meeting.status === 'completed' || meeting.status === 'transcribed' ? 'success'
                                    : meeting.status === 'cancelled' ? 'danger'
                                      : 'warning'
                              }
                              className="text-[10px] flex-shrink-0"
                            >
                              {meeting.status === 'scheduled' ? 'Запланирована'
                                : meeting.status === 'ongoing' ? 'Идёт'
                                  : meeting.status === 'completed' ? 'Завершена'
                                    : meeting.status === 'transcribed' ? 'Транскрибирована'
                                      : 'Отменена'}
                            </Badge>
                            <ExternalLink className="w-3.5 h-3.5 text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-text-tertiary">Встреч нет</p>
                    )}
                  </div>

                  <div className="h-px bg-white/10" />

                  {/* Specification with Versioning */}
                  <SpecificationVersions
                    leadId={leadId}
                    currentSpec={specification}
                    onSpecChange={setSpecification}
                    onSpecBlur={handleSpecificationBlur}
                  />

                  {/* UTM Data */}
                  {(lead.utmSource || lead.utmMedium || lead.utmCampaign) && (
                    <>
                      <div className="h-px bg-white/10" />
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                          UTM метки
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {lead.utmSource && (
                            <Badge variant="default" className="text-[10px]">
                              source: {lead.utmSource}
                            </Badge>
                          )}
                          {lead.utmMedium && (
                            <Badge variant="default" className="text-[10px]">
                              medium: {lead.utmMedium}
                            </Badge>
                          )}
                          {lead.utmCampaign && (
                            <Badge variant="default" className="text-[10px]">
                              campaign: {lead.utmCampaign}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Actions */}
                  {lead.status !== 'converted' && lead.status !== 'lost' && (
                    <>
                      <div className="h-px bg-white/10" />
                      <div className="flex gap-2">
                        <button
                          onClick={handleConvert}
                          className="flex-1 px-4 py-2 bg-accent-lime/15 text-accent-lime rounded-lg text-sm font-medium hover:bg-accent-lime/25 transition-colors"
                        >
                          Конвертировать в проект
                        </button>
                      </div>
                    </>
                  )}

                  {/* Created date */}
                  <div className="h-px bg-white/10" />
                  <div className="text-xs text-text-tertiary">
                    Создана {formatDate(lead.createdAt)}
                  </div>
                </div>
              </>
            )}
          </motion.div>

          {/* Delete Confirmation */}
          {lead && (
            <ConfirmModal
              isOpen={showDeleteConfirm}
              title="Удалить заявку?"
              message={`Вы уверены, что хотите удалить заявку "${lead.name}"? Это действие нельзя отменить.`}
              confirmText="Удалить"
              cancelText="Отмена"
              variant="danger"
              onConfirm={handleDelete}
              onCancel={() => setShowDeleteConfirm(false)}
              loading={deleteLead.isPending}
            />
          )}

          {/* Lost Reason Modal */}
          <LostReasonModal
            isOpen={showLostReason}
            onClose={() => setShowLostReason(false)}
            onConfirm={handleLostReasonConfirm}
            isLoading={updateLead.isPending}
          />

          {/* Convert Wizard */}
          {lead && (
            <ConvertWizardModal
              lead={lead}
              isOpen={showConvertWizard}
              onClose={() => setShowConvertWizard(false)}
              onSuccess={() => {
                setShowConvertWizard(false)
                onClose()
              }}
            />
          )}
        </div>
      )}
    </AnimatePresence>
  )
}
